'use client';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ArrowRight from './Icons/ArrowRight';
import { bezierEase } from '@/utils/utils';
import Button from 'components/src/button';
import { ImageWithFocalPoint } from './ImageWithFocalPoint';
import { BentoBoxProps } from '@/types';
import typewriter, { Placement, Title } from '@/analytics/segment';
import { useAtom, useSetAtom } from 'jotai';
import { callMePrependTextAtom, isCallMeModalOpenAtom, isDoBusinessWithUsFormAtom } from '@/store';
import { activeAudioAtom, activeRadioAtom, isPlayingRadioAtom } from '@/store';
import clsx from 'clsx';
import { fetchContactFormById } from '@/clients/BentoBoxUtils';
import { BentoBox as BentoBoxType } from 'types/generated';

const IconTransition = ({ isOpen }: { isOpen: boolean }) => {
  return (
    <motion.svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      initial={false}
      animate={isOpen ? 'open' : 'closed'}
    >
      <motion.path
        variants={{
          closed: { d: 'M12 5V19' },
          open: { d: 'M6 6L18 18' },
        }}
        stroke="#FEF9F9"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <motion.path
        variants={{
          closed: { d: 'M5 12H19' },
          open: { d: 'M6 18L18 6' },
        }}
        stroke="#FEF9F9"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </motion.svg>
  );
};

const mediaElement = ({ box, isPortrait }: { box: BentoBoxType; isPortrait: boolean }) => {
  if (box?.backgroundMedia?.url) {
    return (
      <video
        playsInline
        autoPlay
        loop
        muted
        disablePictureInPicture
        id="nav-media"
        style={{
          minWidth: '100%',
          minHeight: '100%',
          zIndex: 1,
          objectFit: 'cover',
          width: '100%',
          height: 'auto',
          top: '50%',
          transform: ' translateY(-50%)',
          position: 'absolute',
        }}
      >
        <source
          src={
            isPortrait && !!box.portraitBackgroundMedia?.url
              ? box.portraitBackgroundMedia?.url
              : box.backgroundMedia.url || ''
          }
          type="video/mp4"
        />
      </video>
    );
  }
  if (box.backgroundImage?.image?.url) {
    return <ImageWithFocalPoint image={box.backgroundImage} />;
  }
  return <div>{box.backgroundImage?.id}</div>;
};

const BentoBox = ({ box, isFullscreen, OnClick }: BentoBoxProps) => {
  const setIsDoBusinessWithUsForm = useSetAtom(isDoBusinessWithUsFormAtom);
  const setIsCallMeModalOpen = useSetAtom(isCallMeModalOpenAtom);
  const setCallMePrependText = useSetAtom(callMePrependTextAtom);
  const setActiveAudio = useSetAtom(activeAudioAtom);
  const [radioStation, setRadioStation] = useAtom(activeRadioAtom);
  const [isPlayingRadio, setIsPlayingRadio] = useAtom(isPlayingRadioAtom);
  const bentoBox = box;
  const [showOverlay, setShowOverlay] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    setIsPortrait(ref.current.clientWidth < ref.current.clientHeight);
  }, [ref]);

  if (false) {
    console.log(radioStation, isPlayingRadio);
  }
  const toggleOverlay = () => setShowOverlay((prev) => !prev);

  const handleActionButtonClick = async (action: string) => {
    switch (action) {
      case 'openDoBusinessWithUsModal':
        typewriter.doBusinessWithUsButtonClicked({
          placement: Placement.BentoBox,
          title: Title.KomduÍViðskipti,
        });
        const contactForm = await fetchContactFormById('2LFEpvaXWCMfNhrjdQTYFg');
        if (contactForm) {
          setCallMePrependText('');
          setIsDoBusinessWithUsForm(true);
        }
        return;
      case 'callMeModal':
        typewriter.doBusinessWithUsButtonClicked({
          placement: Placement.BentoBox,
          title: Title.CallMe,
        });
        setCallMePrependText(box?.leadMessage || '');
        setIsCallMeModalOpen(true);
        return;
      case 'openRadio-Bylgjan':
        setIsPlayingRadio(true);

        setRadioStation({
          id: 'BYLGJAN',
          title: 'Bylgjan',
          description: 'Bylgjan',
          color: '',
          hasSchedule: 'true',
          liveUrl: 'https://live.visir.is/hls-radio/bylgjan/playlist.m3u8',
          order: '0',
          photo: '/ci/89a63617-63fa-4fb0-93d9-ac1f2b73d0cd.png',
          logo: '/ci/9a827b13-096f-4667-b6b7-26e1783275ec.png',
        });
        setActiveAudio({
          stationId: 'BYLGJAN',
          url: 'https://live.visir.is/hls-radio/bylgjan/playlist.m3u8',
          isLiveFeed: true,
        });
        return;
      case 'openRadio-FM957':
        setIsPlayingRadio(true);

        setRadioStation({
          id: 'FM957',
          title: 'FM957',
          description: 'Fm957',
          color: '',
          hasSchedule: 'true',
          liveUrl: 'https://live.visir.is/hls-radio/fm957/playlist.m3u8',
          order: '0',
          photo: '/ci/984396b3-51be-4a1e-bc8d-8af91540e2db.png',
          logo: '/ci/12563b65-99e9-4e2e-b65a-95ec385c42ed.png',
        });
        setActiveAudio({
          stationId: 'FM957',
          url: 'https://live.visir.is/hls-radio/fm957/playlist.m3u8',
          isLiveFeed: true,
        });
        return;
      case 'openRadio-X977':
        setIsPlayingRadio(true);

        setRadioStation({
          id: 'X977',
          title: 'X977',
          description: 'X977',
          color: '',
          hasSchedule: 'true',
          liveUrl: 'https://live.visir.is/hls-radio/x977/playlist.m3u8',
          order: '0',
          photo: '/ci/da09b4da-32eb-4793-bf3a-a60bd696d2ce.png',
          logo: '/ci/d769ab42-d2f4-4e80-9e95-7ee8247ff5f2.png',
        });
        setActiveAudio({
          stationId: 'X977',
          url: 'https://live.visir.is/hls-radio/x977/playlist.m3u8',
          isLiveFeed: true,
        });
        return;
      default:
        return; //TODO
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const isTouch = window.matchMedia('(pointer: coarse)').matches;
      const isSmall = window.innerWidth < 768;
      if (isTouch || isSmall) {
        setIsHovered(true); // Simulate hover
      } else {
        setIsHovered(false); // Restore default
      }
    };

    handleResize(); // Run on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={OnClick}
      className={clsx(
        `relative flex h-full w-full items-center justify-center overflow-hidden 
        ${bentoBox.backgroundColor ? `bg-${bentoBox.backgroundColor}` : ''}`,
        {
          'overflow-hidden rounded-28': !isFullscreen,
          'text-neutral-10':
            bentoBox.backgroundColor === 'primary-light-coral' &&
            !(box.backgroundImage?.image?.url || box.backgroundMedia?.url),
          'text-white-100': !(
            bentoBox.backgroundColor === 'primary-light-coral' &&
            !(box.backgroundImage?.image?.url || box.backgroundMedia?.url)
          ),
        }
      )}
    >
      {mediaElement({ box: bentoBox, isPortrait })}
      {bentoBox?.callOutSticker && (
        <div className="z-100 absolute right-[62px] top-[41px]">
          <div className="flex h-[160px] w-[160px] flex-col items-center justify-center rounded-full bg-red-100">
            <p className="body-lg">{bentoBox?.callOutStickerOverline}</p>
            <p className="headline-lg">{bentoBox?.callOutStickerTitle}</p>
            <p className="body-lg font-bold">{bentoBox?.callOutStickerSubtitle}</p>
          </div>
        </div>
      )}

      <div className={clsx('absolute z-10 h-full w-full', { 'brand-fade': bentoBox.imageGradient })}>
        {!isFullscreen && bentoBox?.informationText && (
          <div
            onClick={toggleOverlay}
            className="absolute right-8 top-8 z-20 flex h-56 w-56 cursor-pointer items-center justify-center rounded-full bg-[#2c2d301e]"
          >
            <IconTransition isOpen={showOverlay} />
          </div>
        )}

        {/* Main Content */}
        <div className={`${isFullscreen ? 'p-64 md:p-100' : 'p-32'} flex min-h-full w-full justify-between rounded-12`}>
          {/* Left Column with animated text and button */}
          <div className="flex w-full flex-col justify-between">
            <div>
              <p className="text-md font-bold">{bentoBox?.companyBrandSubrand ? bentoBox?.companyBrandSubrand : ''}</p>
            </div>

            <motion.div layout className="flex flex-col">
              {bentoBox?.tags && bentoBox.tags.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {bentoBox.tags.map((tag, index) => (
                    <motion.p
                      layout
                      key={index}
                      style={{ border: '1px solid rgba(56, 4, 10, 0.12)' }}
                      className="body-md rounded-full px-16 py-8"
                    >
                      {tag}
                    </motion.p>
                  ))}
                </div>
              )}
              <motion.p layout className="body-lg">
                {bentoBox?.overline}
              </motion.p>
              <motion.p layout className="headline-md font-bold">
                {bentoBox?.title}
              </motion.p>
              <motion.p layout className="text-md font-bold">
                {bentoBox?.subtitle}
              </motion.p>

              <AnimatePresence mode="wait">
                {isHovered && bentoBox?.actionButton && (
                  <motion.div
                    key="action-button"
                    layout
                    initial={{ opacity: 0, y: 20, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, y: 0, height: '60px', marginTop: '16px' }}
                    exit={{ opacity: 0, y: 20, height: 0, marginTop: 0 }}
                    transition={{ duration: 0.24, ease: bezierEase }}
                    className="overflow-hidden px-2"
                  >
                    {(bentoBox?.actionButtonAction === 'None' || !bentoBox?.actionButtonAction) && (
                      <a href={bentoBox?.actionButtonUrl || '#'} onClick={(e) => e.stopPropagation()}>
                        <Button
                          label={bentoBox?.actionButtonText || ''}
                          variant="primary"
                          iconPosition="right"
                          icon={<ArrowRight />}
                        />
                      </a>
                    )}
                    {bentoBox?.actionButtonAction && bentoBox?.actionButtonAction !== 'None' && (
                      <Button
                        label={bentoBox?.actionButtonText || ''}
                        onClick={() => handleActionButtonClick(bentoBox?.actionButtonAction || '')}
                        variant="primary"
                        iconPosition="right"
                        icon={<ArrowRight />}
                      />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>

        {/* Expanding Circle Overlay */}
        <AnimatePresence>
          {showOverlay && bentoBox.informationText && (
            <motion.div
              initial={{ clipPath: 'circle(0% at 100% 0%)' }}
              animate={{ clipPath: 'circle(150% at 100% 0%)' }}
              exit={{ clipPath: 'circle(0% at 100% 0%)' }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              className="absolute left-0 top-0 z-10 flex h-full w-full flex-col items-center overflow-auto bg-red-100 p-32 pt-64 text-white-100 md:justify-center md:pt-0"
            >
              <p
                className="text-md font-bold"
                dangerouslySetInnerHTML={{
                  __html: bentoBox?.informationText?.replace(/\n/g, '<br />') || '',
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BentoBox;
